import { 
  NewProjectFormData,
  NewSubProject,
  NewProjectValidationState,
  NewProjectValidationRules
} from '../types/newProject';

const validationRules: NewProjectValidationRules = {
  project: {
    name: { required: true, minLength: 3, maxLength: 100 },
    description: { required: true, minLength: 10, maxLength: 1000 },
    startDate: { required: true },
    endDate: { required: true, afterStartDate: true },
    zone: { required: true },
    image: { required: false, maxFiles: 5, maxSize: 5 * 1024 * 1024 } 
  },
  subProject: {
    name: { required: true, minLength: 3, maxLength: 100 },
    description: { required: true, minLength: 10, maxLength: 500 },
    startDate: { required: true },
    endDate: { required: true, afterStartDate: true }
  }
};

export const newValidationService = {
  validateProjectData: (projectData: NewProjectFormData): boolean => {
    const rules = validationRules.project;
    
    if (rules.name.required && (!projectData.name || projectData.name.trim().length === 0)) {
      return false;
    }
    if (projectData.name && (projectData.name.length < (rules.name.minLength || 0) || 
        projectData.name.length > (rules.name.maxLength || Infinity))) {
      return false;
    }
    
    if (rules.description.required && (!projectData.description || projectData.description.trim().length === 0)) {
      return false;
    }
    if (projectData.description && (projectData.description.length < (rules.description.minLength || 0) || 
        projectData.description.length > (rules.description.maxLength || Infinity))) {
      return false;
    }
    
    if (rules.startDate.required && !projectData.startDate) {
      return false;
    }
    if (rules.endDate.required && !projectData.endDate) {
      return false;
    }

    if (projectData.startDate && projectData.endDate) {
      const startDate = new Date(projectData.startDate);
      const endDate = new Date(projectData.endDate);
      if (startDate >= endDate) {
        return false;
      }
    }
    
    if (rules.zone.required && (!projectData.zone || projectData.zone.trim().length === 0)) {
      return false;
    }
    
    return true;
  },

  validateSubProjectData: (subProject: NewSubProject): boolean => {
    const rules = validationRules.subProject;
    
    if (rules.name.required && (!subProject.name || subProject.name.trim().length === 0)) {
      return false;
    }
    if (subProject.name && (subProject.name.length < (rules.name.minLength || 0) || 
        subProject.name.length > (rules.name.maxLength || Infinity))) {
      return false;
    }
    
    if (rules.description.required && (!subProject.description || subProject.description.trim().length === 0)) {
      return false;
    }
    if (subProject.description && (subProject.description.length < (rules.description.minLength || 0) || 
        subProject.description.length > (rules.description.maxLength || Infinity))) {
      return false;
    }
    
    if (rules.startDate.required && !subProject.startDate) {
      return false;
    }
    if (rules.endDate.required && !subProject.endDate) {
      return false;
    }
    
    if (subProject.startDate && subProject.endDate) {
      const startDate = new Date(subProject.startDate);
      const endDate = new Date(subProject.endDate);
      if (startDate >= endDate) {
        return false;
      }
    }
    
    return true;
  },

  getValidationErrors: (projectData: NewProjectFormData, subProjects: NewSubProject[]): NewProjectValidationState => {
    const errors: NewProjectValidationState = {
      project: {},
      subProjects: {},
      global: {}
    };
    
    const rules = validationRules.project;
    
    if (rules.name.required && (!projectData.name || projectData.name.trim().length === 0)) {
      errors.project.name = 'El nombre del proyecto es requerido';
    } else if (projectData.name && projectData.name.length < (rules.name.minLength || 0)) {
      errors.project.name = `El nombre debe tener al menos ${rules.name.minLength} caracteres`;
    } else if (projectData.name && projectData.name.length > (rules.name.maxLength || Infinity)) {
      errors.project.name = `El nombre no puede tener más de ${rules.name.maxLength} caracteres`;
    }
    
    if (rules.description.required && (!projectData.description || projectData.description.trim().length === 0)) {
      errors.project.description = 'La descripción del proyecto es requerida';
    } else if (projectData.description && projectData.description.length < (rules.description.minLength || 0)) {
      errors.project.description = `La descripción debe tener al menos ${rules.description.minLength} caracteres`;
    } else if (projectData.description && projectData.description.length > (rules.description.maxLength || Infinity)) {
      errors.project.description = `La descripción no puede tener más de ${rules.description.maxLength} caracteres`;
    }
    
    if (rules.startDate.required && !projectData.startDate) {
      errors.project.startDate = 'La fecha de inicio es requerida';
    }
    
    if (rules.endDate.required && !projectData.endDate) {
      errors.project.endDate = 'La fecha de finalización es requerida';
    }
    
    if (projectData.startDate && projectData.endDate) {
      const startDate = new Date(projectData.startDate);
      const endDate = new Date(projectData.endDate);
      if (startDate >= endDate) {
        errors.project.endDate = 'La fecha de finalización debe ser posterior a la fecha de inicio';
      }
    }
    
    if (rules.zone.required && (!projectData.zone || projectData.zone.trim().length === 0)) {
      errors.project.zone = 'La zona es requerida';
    }
    
    if (subProjects.length === 0) {
      errors.global.hasSubProjects = 'Debe agregar al menos un subproyecto';
    } else {
      subProjects.forEach((subProject, index) => {
        const subProjectErrors: any = {};
        const subRules = validationRules.subProject;
        
        if (subRules.name.required && (!subProject.name || subProject.name.trim().length === 0)) {
          subProjectErrors.name = 'El nombre del subproyecto es requerido';
        } else if (subProject.name && subProject.name.length < (subRules.name.minLength || 0)) {
          subProjectErrors.name = `El nombre debe tener al menos ${subRules.name.minLength} caracteres`;
        } else if (subProject.name && subProject.name.length > (subRules.name.maxLength || Infinity)) {
          subProjectErrors.name = `El nombre no puede tener más de ${subRules.name.maxLength} caracteres`;
        }
        
        if (subRules.description.required && (!subProject.description || subProject.description.trim().length === 0)) {
          subProjectErrors.description = 'La descripción del subproyecto es requerida';
        } else if (subProject.description && subProject.description.length < (subRules.description.minLength || 0)) {
          subProjectErrors.description = `La descripción debe tener al menos ${subRules.description.minLength} caracteres`;
        } else if (subProject.description && subProject.description.length > (subRules.description.maxLength || Infinity)) {
          subProjectErrors.description = `La descripción no puede tener más de ${subRules.description.maxLength} caracteres`;
        }
        
        if (subRules.startDate.required && !subProject.startDate) {
          subProjectErrors.startDate = 'La fecha de inicio es requerida';
        }
        
        if (subRules.endDate.required && !subProject.endDate) {
          subProjectErrors.endDate = 'La fecha de finalización es requerida';
        }
        
        if (subProject.startDate && subProject.endDate) {
          const startDate = new Date(subProject.startDate);
          const endDate = new Date(subProject.endDate);
          if (startDate >= endDate) {
            subProjectErrors.endDate = 'La fecha de finalización debe ser posterior a la fecha de inicio';
          }
        }
        
        if (Object.keys(subProjectErrors).length > 0) {
          errors.subProjects[subProject.id || index.toString()] = subProjectErrors;
        }
      });
    }
    
    return errors;
  },

  validateDateRange: (startDate: string, endDate: string): boolean => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return start < end;
  },

  validateMinDate: (date: string, minDate: string): boolean => {
    if (!date || !minDate) return false;
    const dateObj = new Date(date);
    const minDateObj = new Date(minDate);
    return dateObj >= minDateObj;
  },

  validateFile: (file: File, maxSize: number, allowedTypes: string[]): { isValid: boolean; error?: string } => {
    if (!file) {
      return { isValid: false, error: 'No se ha seleccionado ningún archivo' };
    }
    
    if (file.size > maxSize) {
      return { isValid: false, error: `El archivo es demasiado grande. Máximo ${maxSize / (1024 * 1024)}MB` };
    }
    
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: `Tipo de archivo no permitido. Tipos permitidos: ${allowedTypes.join(', ')}` };
    }
    
    return { isValid: true };
  },

  getValidationRules: (): NewProjectValidationRules => {
    return validationRules;
  },

  validateCompleteProject: (projectData: NewProjectFormData, subProjects: NewSubProject[]): boolean => {
    const isProjectValid = newValidationService.validateProjectData(projectData);
    const hasSubProjects = subProjects.length > 0;
    const areSubProjectsValid = subProjects.every(sub => newValidationService.validateSubProjectData(sub));
    
    return isProjectValid && hasSubProjects && areSubProjectsValid;
  }
};
